/**
 * GoalsFilterToolbar Component (Epic 18 — /goals)
 *
 * Visual clone of ClientFilterToolbar (search input + multi-tab filter dropdown
 * popover + ViewSwitcher + Create button + active-filter chip row), adapted for
 * the goals surface. It mirrors the goals route's URL-synced filter state
 * (GoalsSearch: client / status / interval), plus an optional metric/platform
 * filter, AND a NET-NEW client-side name search.
 *
 * 🔴 Single-select semantics (vs clients' multi-select arrays): the goals route
 * stores scalar search params (one client, one status, one interval, one
 * metric/platform). Selecting a value in a tab REPLACES that filter; clicking
 * the already-selected row CLEARS it. The dropdown chrome is otherwise byte-
 * for-byte the clients toolbar.
 *
 * ViewSwitcher + ClientViewMode are imported directly from ../clients/ViewSwitcher
 * (the toggle is generic + stateless — never re-rolled here).
 */

import { useState } from "react";
import { Input, Button, cn } from "@provenleap/ui";
import {
  MagnifyingGlass,
  X,
  Funnel,
  Check,
  CaretDown,
  Target,
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "motion/react";
import { ViewSwitcher, type ClientViewMode } from "../clients/ViewSwitcher";
import { ClientAvatar } from "../clients/ClientAvatar";
import { GoalsViewToggle } from "./GoalsViewToggle";
import { ColumnVisibilityMenu } from "../shared/columnVisibility";
import { GOAL_HIDEABLE_COLUMNS } from "./goalColumns";
import type { GoalsView } from "../../routes/_authenticated/goals/index";
import { PICKER_PLATFORMS, logoUrl } from "../../lib/integrations";
import {
  GOAL_STATUS_VALUES,
  GOAL_STATUS_BADGES,
  GOAL_PERIOD_TYPES,
  INTERVAL_BADGES,
  type GoalStatus,
  type GoalPeriodType,
} from "../../lib/goalPageDisplay";

/** A {id,name} pair for the Client filter tab (mirrors the route's clientOptions). */
export interface GoalClientOption {
  id: string;
  name: string;
}

type FilterTab = "status" | "client" | "interval" | "metric";

export interface GoalsFilterToolbarProps {
  /** NET-NEW free-text search (filters goal.name client-side in the route). */
  search: string;
  onSearchChange: (value: string) => void;

  /** Single-select scalar filters — mirror GoalsSearch. `undefined` = no constraint. */
  client?: string;
  onClientChange: (client: string | undefined) => void;
  clientOptions: GoalClientOption[];

  status?: GoalStatus;
  onStatusChange: (status: GoalStatus | undefined) => void;

  interval?: GoalPeriodType;
  onIntervalChange: (interval: GoalPeriodType | undefined) => void;

  /**
   * Optional metric/platform filter (the prompt's "by metric" axis maps to a
   * goal's `platform`). Omit `metricOptions` (or pass empty) to hide the tab.
   */
  metric?: string;
  onMetricChange?: (metric: string | undefined) => void;
  metricOptions?: string[];

  onClearFilters: () => void;

  viewMode: ClientViewMode;
  onViewModeChange: (mode: ClientViewMode) => void;

  /**
   * Active ⇄ History view switch (Story 18.7). Rendered in the right cluster,
   * styled like the clients page's "Show Archived" button. Flips its label like
   * Show/Hide Archived (see GoalsViewToggle).
   */
  activeView: GoalsView;
  onViewChange: (view: GoalsView) => void;

  /** Create button (plan-gated in the route). */
  canManage?: boolean;
  onCreateGoal?: () => void;
  createDisabled?: boolean;

  /** Column-visibility (table view only). Hidden column ids + toggle handler. */
  hiddenColumns?: string[];
  onToggleColumn?: (id: string) => void;
  /** Show the "Columns" control — only relevant in the table view. */
  showColumnsControl?: boolean;
}

/** Lowercased status label for the inner-search match + plain-text rows. */
function statusLabel(status: GoalStatus): string {
  return status.replace("_", " ");
}

export function GoalsFilterToolbar({
  search,
  onSearchChange,
  client,
  onClientChange,
  clientOptions,
  status,
  onStatusChange,
  interval,
  onIntervalChange,
  metric,
  onMetricChange,
  metricOptions = [],
  onClearFilters,
  viewMode,
  onViewModeChange,
  activeView,
  onViewChange,
  canManage = false,
  onCreateGoal,
  createDisabled = false,
  hiddenColumns = [],
  onToggleColumn,
  showColumnsControl = false,
}: GoalsFilterToolbarProps) {
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<FilterTab>("status");
  const [filterSearch, setFilterSearch] = useState("");

  const showMetricTab = metricOptions.length > 0 && !!onMetricChange;

  // Single-select cardinality: each active scalar contributes 1 to the count.
  const totalFilterCount =
    (status ? 1 : 0) +
    (client ? 1 : 0) +
    (interval ? 1 : 0) +
    (metric && showMetricTab ? 1 : 0);

  const tabs: { key: FilterTab; label: string; count: number }[] = [
    { key: "status", label: "Status", count: status ? 1 : 0 },
    { key: "client", label: "Client", count: client ? 1 : 0 },
    { key: "interval", label: "Interval", count: interval ? 1 : 0 },
    ...(showMetricTab
      ? [{ key: "metric" as const, label: "Metric", count: metric ? 1 : 0 }]
      : []),
  ];

  const selectedClientName = clientOptions.find((c) => c.id === client)?.name;

  // Inner-search placeholder per active tab (mirrors clients' computed string).
  const innerSearchNoun =
    activeTab === "status"
      ? "statuses"
      : activeTab === "client"
        ? "clients"
        : activeTab === "interval"
          ? "intervals"
          : "metrics";

  return (
    <div className="mb-4 space-y-2">
      {/* Row 1: Search + Filter + View + Create */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search input */}
        <div className="relative max-w-xs flex-1">
          <MagnifyingGlass
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground"
          />
          <Input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search goals..."
            aria-label="Search goals"
            className="pl-9 pr-9 rounded-2xl text-foreground placeholder:text-foreground shadow-xs text-[13.5px]"
            style={{ border: "0.5px solid var(--hairline)" }}
          />
          {search && (
            <button
              type="button"
              onClick={() => onSearchChange("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 min-h-11 min-w-11 flex items-center justify-center text-foreground/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Columns dropdown — table view only (show/hide table columns) */}
        {showColumnsControl && onToggleColumn && (
          <ColumnVisibilityMenu
            columns={GOAL_HIDEABLE_COLUMNS}
            hiddenColumns={hiddenColumns}
            onToggleColumn={onToggleColumn}
          />
        )}

        {/* Filters dropdown */}
        <div className="relative">
          <Button
            variant="outline"
            className="shrink-0 rounded-2xl text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
            style={{ border: "0.5px solid var(--hairline)" }}
            onClick={() => {
              setFilterOpen(!filterOpen);
              setFilterSearch("");
            }}
          >
            <Funnel size={16} className="mr-1" />
            Filters
            {totalFilterCount > 0 && (
              <span className="ml-1 bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {totalFilterCount}
              </span>
            )}
            <CaretDown size={12} className="ml-1" />
          </Button>

          <AnimatePresence>
            {filterOpen && (
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => {
                    setFilterOpen(false);
                    setFilterSearch("");
                  }}
                />

                {/* Dropdown panel */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -4 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -4 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                  className="absolute left-0 top-full mt-2.5 z-50 w-72 bg-popover rounded-2xl shadow-md p-2"
                  style={{ border: "0.5px solid var(--hairline)" }}
                >
                  {/* Arrow pointing up */}
                  <div
                    className="absolute -top-[5px] left-6 w-2.5 h-2.5 bg-popover rotate-45 z-10"
                    style={{
                      borderLeft: "0.5px solid var(--hairline)",
                      borderTop: "0.5px solid var(--hairline)",
                    }}
                  />
                  {/* Tab bar */}
                  <div className="flex mx-4 pb-0">
                    {tabs.map((tab) => (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => {
                          setActiveTab(tab.key);
                          setFilterSearch("");
                        }}
                        className={cn(
                          "flex-1 px-3 py-2.5 text-[13px] font-medium transition-colors cursor-pointer",
                          activeTab === tab.key
                            ? "text-foreground"
                            : "text-muted-foreground hover:text-foreground"
                        )}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Search */}
                  <div className="px-2 pt-2 pb-0.5">
                    <div className="relative">
                      <MagnifyingGlass
                        size={16}
                        className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
                      />
                      <input
                        type="text"
                        value={filterSearch}
                        onChange={(e) => setFilterSearch(e.target.value)}
                        placeholder={`Search ${innerSearchNoun}...`}
                        className="w-full pl-8 pr-3 py-2 text-[13px] text-foreground bg-transparent placeholder:text-muted-foreground placeholder:font-normal font-medium rounded-lg border-[0.5px] border-input focus:outline-none focus-visible:outline-none"
                        style={{ outline: "none", boxShadow: "none" }}
                      />
                    </div>
                  </div>

                  {/* Tab content */}
                  <div className="max-h-[260px] overflow-y-auto pt-2 pb-1">
                    {/* Status tab */}
                    {activeTab === "status" && (
                      <>
                        {GOAL_STATUS_VALUES.filter(
                          (s) =>
                            !filterSearch ||
                            statusLabel(s)
                              .toLowerCase()
                              .includes(filterSearch.toLowerCase())
                        ).map((s) => {
                          const isSelected = status === s;
                          const badge = GOAL_STATUS_BADGES[s];
                          return (
                            <button
                              key={s}
                              type="button"
                              onClick={() =>
                                onStatusChange(isSelected ? undefined : s)
                              }
                              className="flex items-center w-[calc(100%-12px)] ml-1 px-2 py-2 text-[13px] rounded-lg border-[0.9px] border-transparent hover:bg-muted/20 hover:border-input hover:shadow-xs cursor-pointer"
                            >
                              <span
                                className={cn(
                                  "w-4 h-4 border rounded mr-2 flex items-center justify-center shrink-0",
                                  isSelected &&
                                    "bg-primary border-primary text-white"
                                )}
                              >
                                {isSelected && <Check size={12} />}
                              </span>
                              <span
                                className={cn(
                                  "text-[11px] font-semibold rounded-full px-2 py-0.5",
                                  badge.badge
                                )}
                              >
                                {badge.label}
                              </span>
                            </button>
                          );
                        })}
                      </>
                    )}

                    {/* Client tab */}
                    {activeTab === "client" && (
                      <>
                        {clientOptions.length === 0 ? (
                          <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                            No clients yet
                          </div>
                        ) : (
                          [...clientOptions]
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .filter(
                              (c) =>
                                !filterSearch ||
                                c.name
                                  .toLowerCase()
                                  .includes(filterSearch.toLowerCase())
                            )
                            .map((c) => {
                              const isSelected = client === c.id;
                              return (
                                <button
                                  key={c.id}
                                  type="button"
                                  onClick={() =>
                                    onClientChange(isSelected ? undefined : c.id)
                                  }
                                  className="flex items-center w-[calc(100%-12px)] ml-1 px-2 py-2 text-[13px] rounded-lg border-[0.9px] border-transparent hover:bg-muted/20 hover:border-input hover:shadow-xs cursor-pointer"
                                >
                                  <span
                                    className={cn(
                                      "w-4 h-4 border rounded mr-2 flex items-center justify-center shrink-0",
                                      isSelected &&
                                        "bg-primary border-primary text-white"
                                    )}
                                  >
                                    {isSelected && <Check size={12} />}
                                  </span>
                                  <ClientAvatar
                                    name={c.name}
                                    logoUrl={null}
                                    size="sm"
                                    className="!h-4 !w-4 !text-[8px] mr-2"
                                  />
                                  <span className="truncate">{c.name}</span>
                                </button>
                              );
                            })
                        )}
                      </>
                    )}

                    {/* Interval tab */}
                    {activeTab === "interval" && (
                      <>
                        {GOAL_PERIOD_TYPES.filter(
                          (p) =>
                            !filterSearch ||
                            INTERVAL_BADGES[p]
                              .toLowerCase()
                              .includes(filterSearch.toLowerCase()) ||
                            p.toLowerCase().includes(filterSearch.toLowerCase())
                        ).map((p) => {
                          const isSelected = interval === p;
                          return (
                            <button
                              key={p}
                              type="button"
                              onClick={() =>
                                onIntervalChange(isSelected ? undefined : p)
                              }
                              className="flex items-center w-[calc(100%-12px)] ml-1 px-2 py-2 text-[13px] rounded-lg border-[0.9px] border-transparent hover:bg-muted/20 hover:border-input hover:shadow-xs cursor-pointer"
                            >
                              <span
                                className={cn(
                                  "w-4 h-4 border rounded mr-2 flex items-center justify-center shrink-0",
                                  isSelected &&
                                    "bg-primary border-primary text-white"
                                )}
                              >
                                {isSelected && <Check size={12} />}
                              </span>
                              <span className="capitalize">{p}</span>
                            </button>
                          );
                        })}
                      </>
                    )}

                    {/* Metric tab (platform-keyed) */}
                    {activeTab === "metric" && showMetricTab && (
                      <>
                        {metricOptions.length === 0 ? (
                          <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                            No metrics yet
                          </div>
                        ) : (
                          metricOptions
                            .filter((pid) => {
                              if (!filterSearch) return true;
                              const meta = PICKER_PLATFORMS.find(
                                (p) => p.platform === pid
                              );
                              return (meta?.displayName ?? pid)
                                .toLowerCase()
                                .includes(filterSearch.toLowerCase());
                            })
                            .map((platformId) => {
                              const isSelected = metric === platformId;
                              const meta = PICKER_PLATFORMS.find(
                                (p) => p.platform === platformId
                              );
                              return (
                                <button
                                  key={platformId}
                                  type="button"
                                  onClick={() =>
                                    onMetricChange?.(
                                      isSelected ? undefined : platformId
                                    )
                                  }
                                  className="flex items-center w-[calc(100%-12px)] ml-1 px-2 py-2 text-[13px] rounded-lg border-[0.9px] border-transparent hover:bg-muted/20 hover:border-input hover:shadow-xs cursor-pointer"
                                >
                                  <span
                                    className={cn(
                                      "w-4 h-4 border rounded mr-2 flex items-center justify-center shrink-0",
                                      isSelected &&
                                        "bg-primary border-primary text-white"
                                    )}
                                  >
                                    {isSelected && <Check size={12} />}
                                  </span>
                                  {meta?.domain && (
                                    <img
                                      src={logoUrl(meta.domain)}
                                      alt=""
                                      className="w-4 h-4 rounded-full mr-2 shrink-0 object-contain"
                                      loading="lazy"
                                    />
                                  )}
                                  {meta?.displayName ?? platformId}
                                </button>
                              );
                            })
                        )}
                      </>
                    )}
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>

        {/* View switcher + Active/History toggle + Create — pushed to the right */}
        <div className="flex gap-4 sm:ml-auto">
          <ViewSwitcher view={viewMode} onViewChange={onViewModeChange} />

          <GoalsViewToggle activeView={activeView} onViewChange={onViewChange} />

          {canManage && onCreateGoal && (
            <Button
              variant="brand"
              className="shrink-0 rounded-2xl text-[13px] cursor-pointer"
              onClick={onCreateGoal}
              disabled={createDisabled}
              data-testid="create-goal-btn"
            >
              <Target size={16} className="mr-1" />
              Create Goal
            </Button>
          )}
        </div>
      </div>

      {/* Row 2: Active filter chips */}
      {totalFilterCount > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          {/* Status chip */}
          {status && (
            <span className="inline-flex items-center w-fit rounded-2xl bg-background border-[0.5px] border-input px-4 text-[13px] h-10 font-medium text-foreground whitespace-nowrap">
              <span
                className={cn(
                  "text-[11px] font-semibold rounded-full px-2 py-0.5 mr-1.5",
                  GOAL_STATUS_BADGES[status].badge
                )}
              >
                {GOAL_STATUS_BADGES[status].label}
              </span>
              <button
                type="button"
                onClick={() => onStatusChange(undefined)}
                className="ml-2.5 text-foreground hover:text-destructive transition-colors leading-none flex items-center shrink-0 cursor-pointer"
                style={{ minWidth: 0, minHeight: 0 }}
                aria-label={`Remove status ${statusLabel(status)}`}
              >
                <X size={14} />
              </button>
            </span>
          )}

          {/* Client chip */}
          {client && (
            <span className="inline-flex items-center w-fit rounded-2xl bg-background border-[0.5px] border-input px-4 text-[13px] h-10 font-medium text-foreground whitespace-nowrap">
              {selectedClientName ?? client}
              <button
                type="button"
                onClick={() => onClientChange(undefined)}
                className="ml-2.5 text-foreground hover:text-destructive transition-colors leading-none flex items-center shrink-0 cursor-pointer"
                style={{ minWidth: 0, minHeight: 0 }}
                aria-label={`Remove client ${selectedClientName ?? client}`}
              >
                <X size={14} />
              </button>
            </span>
          )}

          {/* Interval chip */}
          {interval && (
            <span className="inline-flex items-center w-fit rounded-2xl bg-background border-[0.5px] border-input px-4 text-[13px] h-10 font-medium text-foreground whitespace-nowrap capitalize">
              {interval}
              <button
                type="button"
                onClick={() => onIntervalChange(undefined)}
                className="ml-2.5 text-foreground hover:text-destructive transition-colors leading-none flex items-center shrink-0 cursor-pointer"
                style={{ minWidth: 0, minHeight: 0 }}
                aria-label={`Remove interval ${interval}`}
              >
                <X size={14} />
              </button>
            </span>
          )}

          {/* Metric chip */}
          {metric && showMetricTab && (
            <span className="inline-flex items-center w-fit rounded-2xl bg-background border-[0.5px] border-input px-4 text-[13px] h-10 font-medium text-foreground whitespace-nowrap">
              {(() => {
                const meta = PICKER_PLATFORMS.find((p) => p.platform === metric);
                return (
                  <>
                    {meta?.domain && (
                      <img
                        src={logoUrl(meta.domain)}
                        alt=""
                        className="w-3.5 h-3.5 rounded-full mr-1.5 object-contain"
                        loading="lazy"
                      />
                    )}
                    {meta?.displayName ?? metric}
                  </>
                );
              })()}
              <button
                type="button"
                onClick={() => onMetricChange?.(undefined)}
                className="ml-2.5 text-foreground hover:text-destructive transition-colors leading-none flex items-center shrink-0 cursor-pointer"
                style={{ minWidth: 0, minHeight: 0 }}
                aria-label={`Remove metric ${metric}`}
              >
                <X size={14} />
              </button>
            </span>
          )}

          <button
            type="button"
            onClick={onClearFilters}
            className="text-[13px] hover:text-foreground min-h-[44px] px-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
            style={{ color: "#5A7396" }}
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}
